'use client';

import { useCallback, useState } from 'react';
import { DerivativeAngleReplace } from '@/features/angle';
import { useDerivativeAngleData } from '../hooks/use-derivative-angle-data';
import { DerivativeAnglesHeader } from './DerivativeAnglesHeader';
import { DerivativeAngleCard } from './DerivativeAngleCard';
import { useProjectAngles } from '@/shared/api/hooks/use-project-angles';
import { usePaywallPayload } from '@/features/subscription/model/subscription.selectors';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import type { ConceptAngle, Angle } from '@/types/concept';
import type { Concept } from '@/types/concept';
import type { Project } from '@/types/project';

interface DerivativeAnglesViewProps {
  project: Project;
  concept: Concept;
  mainAngle: ConceptAngle | undefined;
  conceptAngles: ConceptAngle[];
  derivativeAngles: ConceptAngle[];
  angles: Record<string, Angle>;
  limits: { angles?: number } | null;
  isArchived?: boolean;
  onAngleUploadSuccess: () => Promise<void>;
  onDeleteAngle: (
    conceptAngleId: string,
    angleId: string | undefined
  ) => Promise<void>;
  onDeleteRender: (renderId: string) => void;
  hideRenderButton?: boolean;
  onAddAngle?: () => void;
  // Two-column layout shows ONE selected derivative — section header is
  // visually superfluous there. Mirrors Apple's two-column behavior.
  hideHeader?: boolean;
}

export function DerivativeAnglesView({
  project,
  concept,
  mainAngle,
  conceptAngles,
  derivativeAngles,
  angles,
  onAngleUploadSuccess,
  onDeleteAngle,
  onDeleteRender,
  hideRenderButton = false,
  onAddAngle,
  hideHeader = false,
}: DerivativeAnglesViewProps) {
  const [replaceAngleData, setReplaceAngleData] = useState<{
    conceptAngleId: string;
    angleName: string;
    hasRenders: boolean;
  } | null>(null);

  // Project-library angle count — pre-flight source of truth so we never
  // send uploads that the server would reject with 422.
  const { data: projectAngles } = useProjectAngles(project.id);
  const projectAngleLibraryCount = projectAngles?.length ?? 0;
  const angleLibraryPayload = usePaywallPayload('ANGLE_LIBRARY_LIMIT_EXCEEDED');
  const showPaywall = useLimitPaywallStore((s) => s.showPaywall);

  const showProjectAngleLibraryUpgrade = useCallback(() => {
    if (angleLibraryPayload) {
      showPaywall(angleLibraryPayload, 'ANGLE_LIBRARY_LIMIT_EXCEEDED');
    }
  }, [angleLibraryPayload, showPaywall]);

  const { hasMainRender, getAngleRenderData, setSelectedRenderId } =
    useDerivativeAngleData({
      conceptId: concept.id,
      mainAngleId: mainAngle?.id,
      derivativeAngles,
    });

  return (
    <>
      {/* Top spacing: two-column mode (hideHeader) sits directly under the
          angle switcher, so we drop the section gap; single-column keeps the
          original "main angle / other angles" visual break. */}
      <div className={hideHeader ? '' : 'mt-12 mb-8'}>
        {!hideHeader && (
          <DerivativeAnglesHeader
            isEmpty={derivativeAngles.length === 0}
            onAdd={onAddAngle}
          />
        )}

        <div className="space-y-6">
          {derivativeAngles.map((angle) => {
            const angleData = angle.relationships?.angle?.data?.id
              ? angles[angle.relationships.angle.data.id]
              : undefined;

            const renderData = getAngleRenderData(angle);

            return (
              <div key={angle.id} className="scroll-mt-6">
              <DerivativeAngleCard
                project={project}
                concept={concept}
                angle={angle}
                angleData={angleData}
                renderData={renderData}
                hasMainRender={hasMainRender}
                hideRenderButton={hideRenderButton}
                onAngleUploadSuccess={onAngleUploadSuccess}
                onDeleteAngle={onDeleteAngle}
                onDeleteRender={onDeleteRender}
                onReplacePhoto={setReplaceAngleData}
                setSelectedRenderId={setSelectedRenderId}
              />
              </div>
            );
          })}
        </div>
      </div>

      {replaceAngleData && (
        <DerivativeAngleReplace
          projectId={project.id}
          conceptId={concept.id}
          conceptAngleId={replaceAngleData.conceptAngleId}
          conceptAngles={conceptAngles}
          angleName={replaceAngleData.angleName}
          hasRenders={replaceAngleData.hasRenders}
          currentProjectAngleLibraryCount={projectAngleLibraryCount}
          onProjectAngleLibraryLimitReached={() => {
            setReplaceAngleData(null);
            showProjectAngleLibraryUpgrade();
          }}
          onSuccess={() => {
            setReplaceAngleData(null);
            onAngleUploadSuccess();
          }}
          onSwapSuccess={() => {
            setReplaceAngleData(null);
            onAngleUploadSuccess();
          }}
          onCancel={() => setReplaceAngleData(null)}
        />
      )}

    </>
  );
}
